import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, } from 'react-native'
import { Button, Text, Spinner, Layout, RangeDatepicker, CheckBox, Datepicker } from '@ui-kitten/components'
import { getProductReportForDays } from '../../global/Firebase/firebase'
import { UserContext } from '../../global/UserProvider'
import { format, parse } from 'date-fns'
import { generateProductReportForDate } from '../../utils/validationFunctions'
import { useTranslation } from 'react-i18next'
import { dateRangeWithTimezoneAndAutoclose } from '../../utils/timezoneFunctions'
import { useAtomValue } from 'jotai'
import { currencyFormatterAtom, localeDateServiceAtom, productCategoriesDictAtom, storesDictAtom, suppliersDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { ModalView } from '../../components/ModalView'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { exportToCSV, exportToExcel } from '../../utils/exportUtils'

const COLUMN_INFO = {
  saleQuantity: {currency: false, fixed: 1},
  saleAmount: {currency: true},
  writeOffQuantity: {currency: false, fixed: 1},
  writeOffAmount: {currency: true},
  supplyQuantity: {currency: false, fixed: 1},
  supplyAmount: {currency: true},
  margin: {currency: true},
  profit: {currency: true},
  checkQuantity: {currency: false},
  purchaseAmount: {currency: true},
  packageAmount: {currency: true},
}

export default function ProductStats({ navigation, route }) {
  const { t } = useTranslation()
  const { initialColumns } = route.params
  const startRange = () => {
    var today = new Date()
    var yesterday = new Date()
    var ago = new Date()
    yesterday.setDate(today.getDate() - 1)
    ago.setDate(today.getDate() - 4)
    return {
      startDate: ago, 
      endDate: yesterday
    }
  }
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const productCategoriesDict = useAtomValue(productCategoriesDictAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const userRole = useAtomValue(userRoleAtom)
  const [data, setData] = useState(null)
  const [columns, setColumns] = useState(initialColumns ? initialColumns : {
    saleAmount: true,
    profit: true,
  })
  const [sortBy, setSortBy] = useState(initialColumns ? Object.keys(initialColumns)[0] : 'saleAmount')
  const [store, setStore] = useState(context.user.store?.id)
  const [range, setRange] = useState(startRange)
  const [foundErrorModalIsShown, setFoundErrorModalIsShown] = useState(false)
  const [validationDate, setValidationDate] = useState(new Date())
  const [isValidationLoading, setIsValidationLoading] = useState(false)
  const _rangePicker = React.createRef()
  const _datepicker = React.createRef()

  useEffect(() => {
    getDataFromFB(range)
  }, [store])

  const getDataFromFB = async (newRange) => {
    if (newRange.startDate != null && newRange.endDate != null) {
      _rangePicker.current.blur()
      setData(null)
      // Collect eligible stores and their per-store range (computed individually,
      // since dateRangeWithTimezoneAndAutoclose is store-specific), then fire all
      // per-store report calls in parallel instead of awaiting them one at a time.
      const eligibleStores = []
      for (let storeId in storesDict) {
        if (store === 'all' || store === storeId) {
          const storeRange = dateRangeWithTimezoneAndAutoclose(newRange, [storeId])
          eligibleStores.push({ storeId, storeRange })
        }
      }
      // get existing previously generated stats and generate for missing days
      const statsPerStore = await Promise.all(
        eligibleStores.map(({ storeId, storeRange }) =>
          getProductReportForDays(db, storeRange, storeId, true)
        )
      )
      const allStats = statsPerStore.reduce((acc, stats) => acc.concat(stats), [])
      const categorizedStats = categorizeStats(allStats)
      setData(categorizedStats)
    }
  }

  const categorizeStats = (stats) => {
    // sum everything by category, by supplier, just a list
    var products = {}
    var byCategories = {}
    var bySuppliers = {}
    for (const [index, value] of stats.entries()) {
      for (var productID in value) {
        const product = value[productID]
        if (product.name != null) {
          if (products[productID] == null) {
            products[productID] = {
              name: product.name,
              categoryID: product.categoryID,
              supplierID: product.supplierID,
            }
          }
          if (byCategories[product.categoryID] == null) {
            byCategories[product.categoryID] = {categoryID: product.categoryID}
          }
          if (bySuppliers[product.supplierID] == null) {
            bySuppliers[product.supplierID] = {supplierID: product.supplierID}
          }
          for (let key in COLUMN_INFO) {
            products[productID][key] = (products[productID][key] || 0) + (product[key] || 0)
            byCategories[product.categoryID][key] = (product[key] || 0) + (byCategories[product.categoryID]?.[key] || 0)
            bySuppliers[product.supplierID][key] = (product[key] || 0) + (bySuppliers[product.supplierID]?.[key] || 0)
          }
        }
      }
    }

    var productsArray = []
    for (let key in products) {
      let newItem = {...products[key]}
      productsArray.push(newItem)
    }
    productsArray.sort((a, b) => b[sortBy] - a[sortBy])

    var byCategoriesArray = []
    for (let key in byCategories) {
      let newItem = {...byCategories[key]}
      newItem['name'] = (key === 'customProduct' ? t('newSale.customProduct') : productCategoriesDict[key]?.name)
      byCategoriesArray.push(newItem)
    }
    byCategoriesArray.sort((a, b) => b[sortBy] - a[sortBy])

    var bySuppliersArray = []
    for (let key in bySuppliers) {
      let newItem = {...bySuppliers[key]}
      newItem['name'] = (key === 'customProduct' ? t('newSale.customProduct') : suppliersDict[key]?.name)
      bySuppliersArray.push(newItem)
    }
    bySuppliersArray.sort((a, b) => b[sortBy] - a[sortBy])

    return {
      productsArray,
      byCategoriesArray,
      bySuppliersArray,
    }
  }

  const handleExport = async (type) => {
    try {
      let exportData = data.productsArray.map(product => ({
        ...product,
        category: productCategoriesDict[product.categoryID]?.name,
        supplier: suppliersDict[product.supplierID]?.name,
      })).map(({ categoryID, supplierID, ...rest }) => rest)

      exportData = exportData.map(item => {
        const translatedItem = {}
        Object.keys(item).forEach(key => {
          translatedItem[t(`stats.${key}`)] = item[key]
        })
        return translatedItem
      })

      const filename = 'product-stats-' + format(range.startDate, 'd-M-y') + '-' + format(range.endDate, 'd-M-y')

      switch (type) {
        case 'csv':
          await exportToCSV(exportData, filename)
          break;
        case 'excel':
          await exportToExcel(exportData, filename)
          break;
      }
    } catch (error) {
      console.error('Export failed:', error)
      // Show error message to user
    }
  }

  return (    
    <ScrollView style={{padding: 20}}>
      <View style={styles.dateFilter}>
        <StoreChooserHorizontal store={store} onChange={(newStore) => {
          setStore(newStore)
        }} />
        <Text category='label' style={styles.label}>
          {t('filterByDate')}
        </Text>
        <RangeDatepicker
          placement='top'
          range={range}
          min={new Date('2020-12-01')}
          max={new Date()}
          ref={_rangePicker}
          dateService={localeDateService}
          onSelect={(newRange) => {
            if (newRange.endDate) {
              setRange(newRange)
              getDataFromFB(newRange)
            } else if (newRange.startDate && range.startDate === newRange.startDate) {
              let endDate = new Date(newRange.startDate)
              endDate.setHours('23', '59', '59', '99')
              setRange({startDate: newRange.startDate, endDate})
              getDataFromFB({startDate: newRange.startDate, endDate})
            } else {
              setRange(newRange)
            }
          }}
        />
      </View>

      { data ? (
        <>
          <Text category='h2' style={{marginTop: 5}}>
            {t('stats.productStats')}
          </Text>
          <Text category='label' style={styles.header}>
            {t('stats.columns')}
          </Text>
          <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
            { Object.entries(COLUMN_INFO).map(([key, value]) => (
              <CheckBox key={key} 
                checked={columns[key]}
                onChange={(value) => {
                  var newR = {...columns}
                  newR[key] = value
                  setColumns(newR)
                }}
                style={styles.columnCheckbox}
              >
                {t('stats.'+key)}
              </CheckBox>
            ))}
          </View>

          <Text category='label' style={styles.header}>
            {t('stats.byProductCategory')}
          </Text>
          <Layout style={styles.tableView}>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.name')}</Text>
              { Object.entries(columns).map(([key, value]) => (
                value && <Text key={key} style={styles.rowItemAmount}>{t('stats.' + key)}</Text>
              ))}
            </View>
            { data.byCategoriesArray.map((item, index) => (
              <View key={item+index} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{item.name}</Text>
                { Object.entries(columns).map(([key, value]) => (
                  value && <Text key={key} style={styles.rowItemAmount}>{(COLUMN_INFO[key].currency ? currencyFormatter.format(item[key]) : item[key]?.toFixed(COLUMN_INFO[key].fixed || 0))}</Text>
                ))}
              </View>
            ))}
          </Layout>

          <Text category='label' style={styles.header}>
            {t('stats.bySupplier')}
          </Text>
          <Layout style={styles.tableView}>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.name')}</Text>
              { Object.entries(columns).map(([key, value]) => (
                value && <Text key={key} style={styles.rowItemAmount}>{t('stats.' + key)}</Text>
              ))}
            </View>
            { data.bySuppliersArray.map((item, index) => (
              <View key={item+index} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{item.name}</Text>
                { Object.entries(columns).map(([key, value]) => (
                  value && <Text key={key} style={styles.rowItemAmount}>{(COLUMN_INFO[key].currency ? currencyFormatter.format(item[key]) : item[key]?.toFixed(COLUMN_INFO[key].fixed || 0))}</Text>
                ))}
              </View>
            ))}
          </Layout>

          <Text category='label' style={styles.header}>
            {t('stats.byProduct')}
          </Text>
          <Layout style={styles.tableView}>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('stats.name')}</Text>
              { Object.entries(columns).map(([key, value]) => (
                value && <Text key={key} style={styles.rowItemAmount}>{t('stats.' + key)}</Text>
              ))}
            </View>
            { data.productsArray.map((item, index) => (
              <View key={item+index} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{item.name}</Text>
                { Object.entries(columns).map(([key, value]) => (
                  value && <Text key={key} style={styles.rowItemAmount}>{(COLUMN_INFO[key].currency ? currencyFormatter.format(item[key]) : item[key]?.toFixed(COLUMN_INFO[key].fixed || 0))}</Text>
                ))}
              </View>
            ))}
          </Layout>
        </>
      ) : (
        <View style={styles.spinner}>
          <Spinner size="large" />
        </View>
      )}

      <ModalView isVisible={foundErrorModalIsShown} setIsVisible={setFoundErrorModalIsShown}>
        <Text style={{marginBottom: 5}} category="h6">{t('stats.foundError')}</Text>
        <Text>{t('stats.foundErrorDescription')}</Text>
        { storesDict && Object.values(storesDict).length > 1 && (
          <>
            <Text category='label' style={styles.label}>{t('store')}</Text>
            <StoreChooserHorizontal store={store} showAll={false} onChange={(newStore) => {
              setStore(newStore)
            }} />
          </>
        )}
        <Text category='label' style={styles.label}>{t('date')}</Text>
        <Datepicker
          placement='top'
          min={new Date('2020-12-01')}
          max={new Date()}
          date={validationDate}
          dateService={localeDateService}
          ref={_datepicker}
          onSelect={(date) => {
            setValidationDate(date)
          }}
          style={{marginBottom: 10}}
        />
        
        { isValidationLoading 
            ? 
              <View style={{alignItems: 'center'}}><Spinner /></View>
            :
              <Button onPress={async () => {
                _datepicker.current.blur()
                setIsValidationLoading(true)
                await generateProductReportForDate(validationDate, store)
                setIsValidationLoading(false)
              }}>{t('stats.recalculate')}</Button>
        }
      </ModalView>
      { (userRole?.permissions?.['exportImport'] || userRole?.id === 'admin') && (
        <>
          <Button appearance="ghost" status='basic' onPress={() => handleExport('csv')}>{t('csvExport')}</Button>
          <Button appearance="ghost" status='basic' onPress={() => handleExport('excel')}>{t('excelExport')}</Button>
        </>
      )}
      <Button appearance="ghost" status='basic' onPress={() => setFoundErrorModalIsShown(true)}>{t('stats.foundError')}</Button>
      <View style={{height: 50}}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  header: {
    marginVertical: 10,
  },
  tableView: {
    padding: 10,
    marginBottom: 10,
    borderRadius: 10,
  },
  rowItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 2,
    borderBottomWidth: 1,
    borderBottomColor: 'gray'
  }, 
  rowItemName: {
    flex: 9
  }, 
  rowItemAmount: {
    flex: 4,
    textAlign: 'right',
    marginRight: 3
  },
  label: {
    marginTop: 5,
    marginBottom: 5,
  },
  spinner: {
    marginTop: 10,
    justifyContent: 'center',
    alignItems: 'center'
  },
  columnCheckbox: {
    marginBottom: 5
  }
})